import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../ui/pagination';
import { useInsuranceStore } from '../../store/insurance';
import { useMembersStore } from 'src/store/members.store';
import { useGroupsStore } from 'src/store/groups';

const Members = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    memberId: number;
    x: number;
    y: number;
  }>({
    isOpen: false,
    memberId: 0,
    x: 0,
    y: 0,
  });
  const [isInitialized, setIsInitialized] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  
  const { plans: allPlans } = useInsuranceStore();
  const { items: groups, loadList: loadGroups } = useGroupsStore();
  const {
    transformedItems,
    loading,
    pagination,
    loadList,
    setSearch,
    setPage,
    setLimit,
    setSort,
    setGroupId,
    setSubscriptionStatus,
    search,
    offset,
    limit,
    column,
    order,
    groupId,
    subscriptionStatus,
  } = useMembersStore();

  // Load groups on mount
  useEffect(() => {
    if (groups.length === 0) {
      loadGroups();
    }
  }, []);

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(pagination.total / limit);

  // Initialize from URL params on mount
  useEffect(() => {
    const searchParam = searchParams.get('search');
    const offsetParam = searchParams.get('offset');
    const limitParam = searchParams.get('limit');
    const columnParam = searchParams.get('column');
    const orderParam = searchParams.get('order');
    const groupIdParam = searchParams.get('groupId');
    const subscriptionStatusParam = searchParams.get('subscriptionStatus');

    // Set search, groupId, and subscriptionStatus first (they reset offset to 0)
    if (searchParam) {
      setSearch(searchParam);
      setSearchInput(searchParam);
    }
    if (groupIdParam) setGroupId(groupIdParam);
    if (subscriptionStatusParam) setSubscriptionStatus(subscriptionStatusParam);
    
    // Set limit (also resets offset to 0)
    if (limitParam) {
      const parsedLimit = parseInt(limitParam);
      if (!isNaN(parsedLimit) && [10, 25, 50, 100].includes(parsedLimit)) {
        setLimit(parsedLimit);
      }
    }
    
    // Set sort (also resets offset to 0)
    if (columnParam && (columnParam === 'id' || columnParam === 'firstName' || columnParam === 'lastName' || columnParam === 'createdAt' || columnParam === 'updatedAt')) {
      if (orderParam && (orderParam === 'asc' || orderParam === 'desc')) {
        setSort(columnParam, orderParam);
      }
    }
    
    // Set offset/page LAST to preserve it after other setters reset it
    const currentLimit = limitParam ? parseInt(limitParam) : limit;
    if (offsetParam) {
      const parsedOffset = parseInt(offsetParam);
      if (!isNaN(parsedOffset)) {
        setPage(Math.floor(parsedOffset / currentLimit) + 1);
      }
    }
    
    // Set initialized in next tick to ensure all store updates are applied
    setTimeout(() => setIsInitialized(true), 0);
  }, []);

  // Debounce search input
  useEffect(() => {
    if (!isInitialized) return;

    const timer = setTimeout(() => {
      const trimmedSearch = searchInput.trim();
      // Only search if length > 2 or empty (to clear search)
      if (trimmedSearch.length > 2 || trimmedSearch.length === 0) {
        setSearch(trimmedSearch);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(timer);
  }, [searchInput, isInitialized]);

  // Update URL when store params change
  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams();
    
    if (search && search.trim()) params.set('search', search.trim());
    if (groupId) params.set('groupId', groupId);
    if (subscriptionStatus) params.set('subscriptionStatus', subscriptionStatus);
    if (offset !== undefined && offset !== null && offset > 0) params.set('offset', offset.toString());
    if (limit !== undefined && limit !== null && limit !== 10) params.set('limit', limit.toString());
    if (column) params.set('column', column);
    if (order) params.set('order', order);

    setSearchParams(params, { replace: true });
  }, [search, offset, limit, column, order, groupId, subscriptionStatus, isInitialized]);

  // Load data
  useEffect(() => {
    if(isInitialized && allPlans?.length > 0){
      loadList(allPlans);
    }
  }, [allPlans?.length, search, offset, limit, column, order, groupId, subscriptionStatus, isInitialized]);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handleSort = (field: 'id' | 'firstName' | 'lastName' | 'createdAt' | 'updatedAt') => {
    if (column === field) {
      setSort(field, order === 'asc' ? 'desc' : 'asc');
    } else {
      setSort(field, 'asc');
    }
  };

  const handleContextMenuClick = (e: React.MouseEvent, memberId: number) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      memberId,
      x: rect.right - 70,
      y: rect.bottom + 2,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({
      isOpen: false,
      memberId: 0,
      x: 0,
      y: 0,
    });
  };

  const handleViewMember = (memberId: number) => {
    navigate(`/members/${memberId}`);
    handleCloseContextMenu();
  };

  // const handleDeleteMember = (memberId: number) => {
  //   // TODO: Implement delete API call
  //   console.log('Delete member:', memberId);
  //   handleCloseContextMenu();
  // };

  const getSortIcon = (field: 'id' | 'firstName' | 'lastName' | 'createdAt' | 'updatedAt') => {
    if (column !== field) {
      return <img src="/icons/sort-arrow.svg" alt="sort" className="w-4 h-4" />;
    }
    return (
      <img
        src="/icons/sort-arrow.svg"
        alt="sort"
        className={`w-4 h-4 ${order === 'asc' ? 'rotate-180' : ''}`}
      />
    );
  };

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5 relative">
      <div className="px-8 py-6 flex items-center justify-between max-lg:flex-row max-xl:items-baseline max-xl:flex-col max-lg:px-2 max-container">
        <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">
          Members
        </h1>
      </div>

          <div className="px-2 pb-6 max-md:px-2 max-container">
            <div className="bg-white-overlay big-corner p-6 max-md:p-0 max-md:bg-transparent">
              <div className="flex justify-between max-md:flex-col items-center mb-6 max-xl:items-baseline max-xl:gap-4">
                <div className="flex gap-4 w-full justify-between flex-wrap">
                  <div className="flex-1 relative max-w-full sm:max-w-xs min-w-[200px]">
                    <input
                      type="text"
                      placeholder="Search by name or member ID..."
                      value={searchInput}
                      onChange={e => setSearchInput(e.target.value)}
                      className="w-full px-4 py-3 pl-8 h-10 border rounded-lg focus:outline-none focus:ring-2 id-card-input"
                    />
                    <img
                      src="/icons/Search.svg"
                      alt="search"
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium whitespace-nowrap">Subs Status</span>
                      <select
                        value={subscriptionStatus || ''}
                        onChange={e => setSubscriptionStatus(e.target.value || undefined)}
                        className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 id-card-input h-10 min-w-[150px]"
                      >
                        <option value="">All</option>
                        <option value="pending">Pending</option>
                        <option value="active">Active</option>
                        <option value="pastDue">Past Due</option>
                        <option value="canceled">Canceled</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium whitespace-nowrap">Group</span>
                      <select
                        value={groupId || ''}
                        onChange={e => setGroupId(e.target.value || undefined)}
                        className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 id-card-input h-10 min-w-[150px]"
                      >
                        <option value="">All</option>
                        {Array.isArray(groups) && groups.map(group => (
                          <option key={String(group.id)} value={String(group.id)}>
                            {group.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium whitespace-nowrap">Show</span>
                      <select
                        value={limit}
                        onChange={e => setLimit(Number(e.target.value))}
                        className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 id-card-input h-10"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="hidden 1xl:block overflow-x-auto 1xl:w-[95%] 2xl:w-full">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('id')}
                      >
                        <div className="flex items-center gap-2">
                          ID {getSortIcon('id')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('firstName')}
                      >
                        <div className="flex items-center gap-2">
                          First Name {getSortIcon('firstName')}
                        </div>
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('lastName')}
                      >
                        <div className="flex items-center gap-2">
                          Last Name {getSortIcon('lastName')}
                        </div>
                      </TableHead>
                      <TableHead>
                        Group Name
                      </TableHead>
                      <TableHead
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('createdAt')}
                      >
                        <div className="flex items-center gap-2">
                          Created {getSortIcon('createdAt')}
                        </div>
                      </TableHead>
                      <TableHead>
                        Contacts
                      </TableHead>
                      <TableHead>
                        Product & Option
                      </TableHead>
                      <TableHead>
                        Amount
                      </TableHead>
                      <TableHead className="text-right"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : transformedItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-8">
                          No members found
                        </TableCell>
                      </TableRow>
                    ) : (
                      transformedItems.map((member) => (
                        <TableRow key={member.id}>
                          <TableCell className="font-medium">
                            {member.id}
                          </TableCell>
                          <TableCell>{member.firstName}</TableCell>
                          <TableCell>{member.lastName}</TableCell>
                          <TableCell>{groups.find(g => g.id === member.groupId)?.name || 'N/A'}</TableCell>
                          <TableCell>
                            {new Date(member.createdAt).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <div className="space-y-1">
                              <div>{member.email}</div>
                              <div>{member.phone || 'N/A'}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {member.plans.map(p => p.name).join(', ') || 'N/A'}
                          </TableCell>
                          <TableCell>${member.sumPrice.toFixed(2)}</TableCell>
                          <TableCell className="text-right">
                            <button
                              onClick={e => handleContextMenuClick(e, member.id)}
                              className="p-2 py-3 border-2 border-gray-200 hover:bg-gray-100 rounded-full"
                            >
                              <img
                                className="min-w-[10px]"
                                src="/icons/dots.svg"
                                alt="dots"
                              />
                            </button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="1xl:hidden space-y-4">
                {loading ? (
                  <div className="text-center py-8">Loading...</div>
                ) : transformedItems.length === 0 ? (
                  <div className="text-center py-8">No members found</div>
                ) : (
                  transformedItems.map((member) => (
                    <div
                      key={member.id}
                      className="bg-white rounded-[20px] border border-gray-200 p-4 shadow-sm"
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <p className="font-bold text-lg mb-1">{member.id}</p>
                          <div className="flex items-center gap-2 max-md:gap-1">
                            <h3 className="font-semibold max-1xl:text-[#71717A] max-md:font-normal text-lg max-2xs:text-sm">
                              {member.firstName} {member.lastName}
                            </h3>
                            <span className="text-gray-400">•</span>
                            <span className="text-sm text-gray-600">
                              {groups.find(g => g.id === member.groupId)?.name || 'N/A'}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={e => handleContextMenuClick(e, member.id)}
                          className="p-2 border border-gray-300 hover:bg-gray-100 rounded-full"
                        >
                          <img
                            className="w-4 h-4"
                            src="/icons/dots.svg"
                            alt="dots"
                          />
                        </button>
                      </div>

                      <div className="space-y-2 text-sm mb-4">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date Created:</span>
                          <span>{new Date(member.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Email:</span>
                          <span className="text-blue-600">{member.email}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Phone Number:</span>
                          <span>{member.phone || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="rounded-xl p-2.5 border border-[#E4E4E7]">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm">{member.plans.map(p => p.name).join(', ') || 'N/A'}</p>
                          </div>
                          <span className="font-semibold">${member.sumPrice.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {!loading && transformedItems.length > 0 && (
                <div className="flex justify-center mt-6">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() =>
                            handlePageChange(Math.max(1, currentPage - 1))
                          }
                          className={
                            currentPage === 1
                              ? 'pointer-events-none opacity-50'
                              : 'cursor-pointer'
                          }
                        />
                      </PaginationItem>

                      {Array.from(
                        { length: Math.min(totalPages, 20) },
                        (_, i) => i + 1
                      ).map(page => (
                        <PaginationItem key={page}>
                          <PaginationLink
                            onClick={() => handlePageChange(page)}
                            isActive={currentPage === page}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      {totalPages > 20 && (
                        <>
                          <PaginationItem>
                            <span className="px-3 py-2">...</span>
                          </PaginationItem>
                          <PaginationItem>
                            <PaginationLink
                              onClick={() => handlePageChange(totalPages)}
                              className="cursor-pointer"
                            >
                              {totalPages}
                            </PaginationLink>
                          </PaginationItem>
                        </>
                      )}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() =>
                            handlePageChange(
                              Math.min(totalPages, currentPage + 1)
                            )
                          }
                          className={
                            currentPage === totalPages || !pagination.hasNextPage
                              ? 'pointer-events-none opacity-50'
                              : 'cursor-pointer'
                          }
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </div>
          </div>

      {contextMenu.isOpen && (
        <div
          className="fixed bg-white card-corner shadow-lg border border-gray-200 z-50 w-fit"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
          }}
        >
          <button
            onClick={() => handleViewMember(contextMenu.memberId)}
            className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            View
          </button>
          {/* <button
            onClick={() => handleDeleteMember(contextMenu.memberId)}
            className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-gray-100 transition-colors whitespace-nowrap"
          >
            Delete
          </button> */}
        </div>
      )}

      {contextMenu.isOpen && (
        <div className="fixed inset-0 z-40" onClick={handleCloseContextMenu} />
      )}
    </div>
  );
};

export default Members;
